/**
 * validateFormDeriveDraft
 *
 * Authoring-time judgment over a draft `FormType.formDerives` array: one pure pass
 * that feeds the Derived-values pane's problem banners, the toolbar's save gate and
 * its two blocking modals, each row's status tag, and the dependency-order toggle.
 * The engine is the authority for validity — an entry absent from
 * `createFormDeriveEngineConfig(...).entries` is invalid regardless of what the
 * reason analysis here concludes, and the analysis only EXPLAINS; when it cannot,
 * the entry is reported `droppedByEngine`, never valid. The one drop rule it names
 * outright is the chained reference hop, read from `formDeriveChainedReferenceHopIds`
 * itself rather than restated. On top of the engine this
 * module refuses what the reconciler's addressability filter would drop, judges
 * cycles through `detectFormDeriveCycle`, and treats a non-empty `missingTableIds`
 * as untrustworthy input that blocks the save. Problems carry an enum plus
 * interpolation values, never screen copy.
 *
 * A row projection owns a whole table, so a problem the engine would answer by
 * dropping the entry costs nine columns rather than one: every save-time rule over
 * `columns` therefore names the COLUMN it fired on, and a cyclic entry carries the
 * SCC's decoded nodes so the drawer can mark the read that condemned the table.
 */

import {
  createFormDeriveEngineConfig,
  detectFormDeriveCycle,
  formDeriveChainedReferenceHopIds,
  formDeriveEntryTableIds,
  formDeriveTargetKey,
  formulaConstantNames,
  formulaFunctionNames,
  formulaOperatorNames,
  isColumnPathTypeId,
  parseFormula,
  UIElementType,
} from '#types';
import type {
  ArColumnAggregation,
  ArColumnDefinition,
  ArFormulaEntry,
  ArTableId,
  ArTableType,
  FieldId,
  FormDeriveEntry,
  FormDeriveGraphNodeKey,
  FormDeriveId,
  FormDeriveProjectionColumn,
} from '#types';

export type ValidateFormDeriveDraftInput = {
  /** The draft `formDerives` array in authored order; validated, never trusted. */
  entries: readonly unknown[];
  /**
   * Every ArTableType the editor holds. A table any entry sources or targets
   * that is absent here surfaces in `missingTableIds` and blocks the save —
   * the caller must fetch it and re-judge rather than report "no cycle".
   */
  tableTypes: readonly ArTableType[];
};

/**
 * Why one draft entry cannot be saved. Each variant mirrors one drop rule of
 * `createFormDeriveEngineConfig`, plus `unaddressableTableId` for the
 * reconciler's `isAddressableId` filter and `droppedByEngine` as the honest
 * fallback when the engine dropped an entry this analysis cannot explain.
 *
 * `columnIndex` is the position in the projection's `columns` list, the identity
 * of a declared column while its `typeId` may be missing or duplicated —
 * `undefined` on `dottedTypeId` alone, where the offending typeId can also be the
 * entry's own partition key. `duplicateTarget` now fires on two derives owning
 * one TABLE, and keeps carrying the entry that would silently win.
 */
export type FormDeriveDraftProblem =
  | { kind: 'invalidDeriveId'; deriveId: string | undefined }
  | { kind: 'duplicateDeriveId'; deriveId: string; firstIndex: number }
  | { kind: 'duplicateTarget'; winnerIndex: number; winnerDeriveId: FormDeriveId }
  /**
   * A dot in one of the four FLAT source positions, per `isColumnTypeId`. The
   * two `isColumnPathTypeId` positions — a declared column and a `sourceCell` —
   * never raise it: there the dot is legal, so a malformed address is
   * `invalidColumnTypeId` instead.
   */
  | { kind: 'dottedTypeId'; typeId: string; columnIndex: number | undefined }
  | { kind: 'unaddressableTableId'; tableId: string }
  | { kind: 'invalidAlias'; alias: string | undefined }
  | { kind: 'duplicateAlias'; alias: string }
  | { kind: 'reservedAlias'; alias: string }
  | { kind: 'expressionUnparsable'; expression: string | undefined }
  | { kind: 'undeclaredVariable'; variable: string }
  | { kind: 'roundToCentsInadmissible' }
  /** A projection declaring nothing writes no cell, so the engine drops it whole. */
  | { kind: 'emptyColumns' }
  /** One table both partitioned and filled; refused here rather than reported as a loop. */
  | { kind: 'projectionSelfTable'; tableId: string }
  /**
   * An address neither `isColumnPathTypeId` position can resolve, raised by the
   * declared column and by a `sourceCell` alike. `typeId` is the offending
   * string, or `undefined` when the position carried no string at all.
   */
  | { kind: 'invalidColumnTypeId'; columnIndex: number; typeId: string | undefined }
  | { kind: 'duplicateColumn'; columnIndex: number; typeId: string; firstIndex: number }
  /** `parent` declared alongside `parent.sub`: two writers on one cell. */
  | { kind: 'columnRootOverlap'; columnIndex: number; typeId: string; partTypeId: string }
  | { kind: 'invalidColumnAlias'; columnIndex: number; alias: string | undefined }
  | { kind: 'duplicateColumnAlias'; columnIndex: number; alias: string; firstIndex: number }
  | { kind: 'reservedColumnAlias'; columnIndex: number; alias: string }
  /** A guard operand naming an alias no EARLIER column of this entry declared. */
  | { kind: 'undeclaredGuardAlias'; columnIndex: number; alias: string }
  | { kind: 'malformedColumn'; columnIndex: number }
  | { kind: 'malformedTarget' }
  | { kind: 'malformedEntry' }
  /**
   * A `rowReferenceProp` column reading a record through a table another
   * projection fills. Named by `formDeriveChainedReferenceHopIds`, which owns the
   * reasoning and is the only spelling of the rule; the engine drops the entry.
   */
  | { kind: 'chainedReferenceHop' }
  | { kind: 'droppedByEngine' };

/**
 * A resolved column on one side of a projection, for warning copy and upgrades.
 * `typeId` is the full address, dots included; `column` is its ROOT definition,
 * because a composite sub-field has no definition of its own on the table type.
 */
export type FormDeriveDraftColumnRef = {
  tableId: ArTableId;
  typeId: string;
  column: ArColumnDefinition;
};

/** Never blocks a save; `columnTypeMismatch` carries both column definitions. */
export type FormDeriveDraftWarning =
  | { kind: 'unusedAlias'; alias: string }
  | {
      kind: 'columnTypeMismatch';
      /** The declared column, or `undefined` when the entry is not a projection. */
      columnIndex: number | undefined;
      source: FormDeriveDraftColumnRef;
      target: FormDeriveDraftColumnRef;
    };

/** `targetFormulaOwned` and `cycle` render as "Not calculating"; copy is the caller's. */
export type FormDeriveDraftEntryStatus = 'active' | 'invalid' | 'targetFormulaOwned' | 'cycle';

export type FormDeriveDraftEntryVerdict = {
  /** Draft array index — the identity, since `deriveId` may be missing or duplicated. */
  index: number;
  deriveId: FormDeriveId | undefined;
  status: FormDeriveDraftEntryStatus;
  /** Non-empty exactly when `status` is `'invalid'`. */
  problems: FormDeriveDraftProblem[];
  warnings: FormDeriveDraftWarning[];
  /**
   * Position in `FormDeriveDraftVerdict.cycles` of the loop this entry sits in,
   * so a drawer can read that loop's `nodes` and mark the column condemned by it
   * without searching. Defined exactly when `status` is `'cycle'`.
   */
  cycleIndex: number | undefined;
};

export type FormDeriveDraftCycleMember = { index: number; deriveId: FormDeriveId };

/**
 * One node of a loop, decoded from `detectFormDeriveCycle`'s `nodeKeys` so the
 * drawer never parses a graph key itself. A `cell` typeId is ROOT-collapsed by the
 * engine, so `parent.a` and `parent.b` arrive as the single node `parent`.
 */
export type FormDeriveDraftCycleNode =
  | { kind: 'field'; fieldId: FieldId }
  | { kind: 'cell'; tableId: ArTableId; typeId: string }
  | { kind: 'rowset'; tableId: ArTableId };

/** One derive-bearing SCC; formula-only components never appear here. */
export type FormDeriveDraftCycle = {
  members: FormDeriveDraftCycleMember[];
  /** Every node of the loop, in the engine's own sorted, deterministic order. */
  nodes: FormDeriveDraftCycleNode[];
};

export type FormDeriveDraftVerdict = {
  /** True when any entry is invalid, any cycle exists, or a referenced table is missing. */
  blocked: boolean;
  /** One verdict per draft entry, in draft order. */
  entries: FormDeriveDraftEntryVerdict[];
  /** The dropped-entry modal payload: the members of `entries` whose status is `'invalid'`. */
  invalidEntries: FormDeriveDraftEntryVerdict[];
  /** The cycle modal payload, straight from the engine's derive-bearing SCCs. */
  cycles: FormDeriveDraftCycle[];
  /** Non-empty means this verdict is not trustworthy; the caller fetches and re-judges. */
  missingTableIds: ArTableId[];
  /** Draft indices of evaluable entries in the engine's execution order, display-only. */
  order: number[];
};

const DERIVE_ID_PATTERN = /^[A-Za-z0-9_-]+$/;

const ALIAS_PATTERN = /^[a-z][a-zA-Z0-9]*$/;

/**
 * Read live off the parser, same three groups as the engine: functions,
 * constants AND named operators. Constants and operators are the dangerous
 * ones — both pass a naive `variables ⊆ aliases` check and are then never read.
 */
const reservedAliasNames = new Set<string>([
  ...formulaFunctionNames,
  ...formulaConstantNames,
  ...formulaOperatorNames,
]);

/** Exhaustive by construction, same device as the engine's own table. */
const columnAggregations: Record<ArColumnAggregation, true> = {
  sum: true,
  avg: true,
  min: true,
  max: true,
  count: true,
};

const orderingOperators = ['<', '<=', '>', '>='];

const malformedEntryProblem: FormDeriveDraftProblem = { kind: 'malformedEntry' };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value !== '';
}

/**
 * The reconciler's own refusal, mirrored from `reconcileFormDerives`'s
 * `isAddressableId`: an id it
 * would interpolate into a Firestore path must not be empty, contain `/`, be
 * `.`/`..`, or match `__*__`. An entry the reconciler drops must be refused at
 * authoring time, because the engine keeps it and nothing else would complain.
 */
function isAddressableId(value: string): boolean {
  return (
    value !== '' &&
    !value.includes('/') &&
    value !== '.' &&
    value !== '..' &&
    !/^__.*__$/.test(value)
  );
}

function readExpressionVariables(expression: string): string[] | undefined {
  try {
    return parseFormula(expression).variables;
  } catch {
    return undefined;
  }
}

function dedupeProblems(problems: FormDeriveDraftProblem[]): FormDeriveDraftProblem[] {
  const seen = new Set<string>();
  return problems.filter((problem) => {
    const key = JSON.stringify(problem);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/**
 * A FLAT read position: `colAggregate.typeId`, the entry's own partition key
 * (`columnIndex` undefined), `groupAggregate.typeId` and
 * `rowReferenceProp.referenceTypeId`. A dot in any of the four addresses the key
 * `cells['parent.sub']`, which never exists — the two positions that do resolve
 * a dot are `columns[].typeId` and `sourceCell.typeId`, judged by
 * `explainSourceCellTypeId` and `isColumnPathTypeId`.
 */
function explainSourceTypeId(
  typeId: unknown,
  columnIndex: number | undefined,
): FormDeriveDraftProblem[] {
  if (!isNonEmptyString(typeId)) {
    return columnIndex === undefined ? [malformedEntryProblem] : [{ kind: 'malformedColumn', columnIndex }];
  }
  return typeId.includes('.') ? [{ kind: 'dottedTypeId', typeId, columnIndex }] : [];
}

/**
 * The `sourceCell` read: the one source position that may address a composite
 * sub-field, because `readFormDeriveCellValue` resolves `parent.sub` to the same
 * `value.fields.<sub>.value` a dotted column writes. A malformed address — `.x`,
 * `x.`, a bare dot — is reported `invalidColumnTypeId`, the same kind the
 * identically malformed DECLARED column raises: both name an address the
 * position cannot resolve, and both carry the offending string so the author is
 * told what they wrote rather than only that something is incomplete. It is not
 * `dottedTypeId`, whose fix would tell the author to drop a dot this position
 * accepts, and not `malformedColumn`, which says an arm is unfinished when the
 * defect is an address that is finished and wrong.
 */
function explainSourceCellTypeId(typeId: unknown, columnIndex: number): FormDeriveDraftProblem[] {
  return isColumnPathTypeId(typeId)
    ? []
    : [
        {
          kind: 'invalidColumnTypeId',
          columnIndex,
          typeId: typeof typeId === 'string' ? typeId : undefined,
        },
      ];
}

function isReferenceProp(value: unknown): boolean {
  return (
    isRecord(value) &&
    (value.kind === 'name' ||
      (value.kind === 'field' && isNonEmptyString(value.fieldId)) ||
      (value.kind === 'reference' && isNonEmptyString(value.referenceKey)))
  );
}

function explainScalarSource(source: Record<string, unknown>): FormDeriveDraftProblem[] {
  if (source.kind === 'field') {
    return isNonEmptyString(source.fieldId) ? [] : [malformedEntryProblem];
  }
  if (source.kind === 'colAggregate') {
    const aggregationValid =
      typeof source.aggregation === 'string' && source.aggregation in columnAggregations;
    const fieldPathValid =
      source.aggregationFieldPath === undefined || typeof source.aggregationFieldPath === 'string';
    return [
      ...(isNonEmptyString(source.tableId) ? [] : [malformedEntryProblem]),
      ...explainSourceTypeId(source.typeId, undefined),
      ...(aggregationValid && fieldPathValid ? [] : [malformedEntryProblem]),
    ];
  }
  if (source.kind === 'referenceProp') {
    return isNonEmptyString(source.referenceKey) && isReferenceProp(source.prop)
      ? []
      : [malformedEntryProblem];
  }
  return [malformedEntryProblem];
}

function explainAlgebraic(value: Record<string, unknown>): FormDeriveDraftProblem[] {
  const operands: unknown[] = Array.isArray(value.operands) ? value.operands : [];

  const operandProblems: FormDeriveDraftProblem[] =
    operands.length === 0
      ? [malformedEntryProblem]
      : operands.flatMap((operand): FormDeriveDraftProblem[] => {
          if (!isRecord(operand)) return [malformedEntryProblem];
          const aliasProblems: FormDeriveDraftProblem[] =
            typeof operand.alias === 'string' && ALIAS_PATTERN.test(operand.alias)
              ? []
              : [
                  {
                    kind: 'invalidAlias',
                    alias: typeof operand.alias === 'string' ? operand.alias : undefined,
                  },
                ];
          const sourceProblems = isRecord(operand.source)
            ? explainScalarSource(operand.source)
            : [malformedEntryProblem];
          return [...aliasProblems, ...sourceProblems];
        });

  const aliases = operands.flatMap((operand) =>
    isRecord(operand) && typeof operand.alias === 'string' && ALIAS_PATTERN.test(operand.alias)
      ? [operand.alias]
      : [],
  );
  const duplicateAliasProblems: FormDeriveDraftProblem[] = [
    ...new Set(aliases.filter((alias, position) => aliases.indexOf(alias) !== position)),
  ].map((alias) => ({ kind: 'duplicateAlias', alias }));
  const reservedAliasProblems: FormDeriveDraftProblem[] = [
    ...new Set(aliases.filter((alias) => reservedAliasNames.has(alias))),
  ].map((alias) => ({ kind: 'reservedAlias', alias }));

  const expression = value.expression;
  const expressionProblems: FormDeriveDraftProblem[] = ((): FormDeriveDraftProblem[] => {
    if (!isNonEmptyString(expression)) {
      return [
        {
          kind: 'expressionUnparsable',
          expression: typeof expression === 'string' ? expression : undefined,
        },
      ];
    }
    const variables = readExpressionVariables(expression);
    if (variables === undefined) return [{ kind: 'expressionUnparsable', expression }];
    const aliasSet = new Set(aliases);
    return [...new Set(variables.filter((variable) => !aliasSet.has(variable)))].map(
      (variable) => ({ kind: 'undeclaredVariable' as const, variable }),
    );
  })();

  const targetProblems: FormDeriveDraftProblem[] =
    isRecord(value.target) && isNonEmptyString(value.target.fieldId)
      ? []
      : [{ kind: 'malformedTarget' }];
  const roundProblems: FormDeriveDraftProblem[] =
    value.roundToCents === undefined || typeof value.roundToCents === 'boolean'
      ? []
      : [malformedEntryProblem];

  return [
    ...operandProblems,
    ...duplicateAliasProblems,
    ...reservedAliasProblems,
    ...expressionProblems,
    ...targetProblems,
    ...roundProblems,
  ];
}

function isCaseLiteral(value: unknown): boolean {
  return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';
}

/**
 * The right side of a condition. `literalsAllowed` is false under an ordering
 * operator, where the type admits a number or an alias and nothing else — a
 * string there is unrepresentable rather than merely undeclared. The object
 * wrapper is what tells the alias `{ alias: 'x' }` from the string literal `'x'`,
 * and an alias is held to the same earlier-only rule as `left`.
 */
function explainCaseOperand(
  operand: unknown,
  columnIndex: number,
  declaredAliases: ReadonlySet<string>,
  literalsAllowed: boolean,
): FormDeriveDraftProblem[] {
  const malformed: FormDeriveDraftProblem[] = [{ kind: 'malformedColumn', columnIndex }];
  if (isRecord(operand)) {
    if (typeof operand.alias !== 'string') return malformed;
    return declaredAliases.has(operand.alias)
      ? []
      : [{ kind: 'undeclaredGuardAlias', columnIndex, alias: operand.alias }];
  }
  if (literalsAllowed) return isCaseLiteral(operand) ? [] : malformed;
  return typeof operand === 'number' ? [] : malformed;
}

function explainCaseCondition(
  condition: unknown,
  columnIndex: number,
  declaredAliases: ReadonlySet<string>,
): FormDeriveDraftProblem[] {
  if (!isRecord(condition)) return [{ kind: 'malformedColumn', columnIndex }];

  const leftProblems: FormDeriveDraftProblem[] =
    typeof condition.left !== 'string'
      ? [{ kind: 'malformedColumn', columnIndex }]
      : declaredAliases.has(condition.left)
        ? []
        : [{ kind: 'undeclaredGuardAlias', columnIndex, alias: condition.left }];

  const equality = condition.op === '==' || condition.op === '!=';
  const ordering = typeof condition.op === 'string' && orderingOperators.includes(condition.op);
  if (!equality && !ordering) return [...leftProblems, { kind: 'malformedColumn', columnIndex }];

  return [
    ...leftProblems,
    ...explainCaseOperand(condition.right, columnIndex, declaredAliases, equality),
  ];
}

function explainCases(
  cases: unknown,
  columnIndex: number,
  declaredAliases: ReadonlySet<string>,
): FormDeriveDraftProblem[] {
  if (!Array.isArray(cases)) return [{ kind: 'malformedColumn', columnIndex }];
  return cases.flatMap((entry): FormDeriveDraftProblem[] => {
    if (!isRecord(entry)) return [{ kind: 'malformedColumn', columnIndex }];
    const valueProblems: FormDeriveDraftProblem[] =
      entry.value === null || isCaseLiteral(entry.value)
        ? []
        : [{ kind: 'malformedColumn', columnIndex }];
    if (entry.when === undefined) return valueProblems;
    if (!Array.isArray(entry.when)) {
      return [...valueProblems, { kind: 'malformedColumn', columnIndex }];
    }
    return [
      ...valueProblems,
      ...entry.when.flatMap((condition) =>
        explainCaseCondition(condition, columnIndex, declaredAliases),
      ),
    ];
  });
}

/**
 * One declared column's value arm. Every typeId it names is a READ of the entry's
 * source table; `sourceCell` alone may address a composite sub-field of one, and
 * `groupAggregate` and `rowReferenceProp` stay undotted. `roundToCents` lives on
 * `groupAggregate` alone, and a flag anywhere else claims a rounding the engine
 * would ignore.
 */
function explainRowValueSource(
  value: unknown,
  columnIndex: number,
  declaredAliases: ReadonlySet<string>,
): FormDeriveDraftProblem[] {
  const malformed: FormDeriveDraftProblem[] = [{ kind: 'malformedColumn', columnIndex }];
  if (!isRecord(value)) return malformed;

  if (value.kind === 'groupKey') return value.roundToCents === undefined ? [] : malformed;

  if (value.kind === 'sourceCell') {
    return [
      ...explainSourceCellTypeId(value.typeId, columnIndex),
      ...(value.roundToCents === undefined ? [] : malformed),
    ];
  }

  if (value.kind === 'groupAggregate') {
    const aggregationValid =
      typeof value.aggregation === 'string' && value.aggregation in columnAggregations;
    const fieldPathValid =
      value.aggregationFieldPath === undefined || typeof value.aggregationFieldPath === 'string';
    const roundValid = value.roundToCents === undefined || typeof value.roundToCents === 'boolean';
    return [
      ...explainSourceTypeId(value.typeId, columnIndex),
      ...(aggregationValid && fieldPathValid && roundValid ? [] : malformed),
    ];
  }

  if (value.kind === 'rowReferenceProp') {
    return [
      ...explainSourceTypeId(value.referenceTypeId, columnIndex),
      ...(isReferenceProp(value.prop) && value.roundToCents === undefined ? [] : malformed),
    ];
  }

  if (value.kind === 'cases') {
    return [
      ...(value.roundToCents === undefined ? [] : malformed),
      ...explainCases(value.cases, columnIndex, declaredAliases),
    ];
  }

  return malformed;
}

type FormDeriveDraftDeclaredColumn = { columnIndex: number; typeId: string };

/**
 * Left to right, because an alias binds only for LATER columns: the alias set
 * grows after the column's own value is judged, so a guard can never read the
 * column it guards. Two target rules beyond uniqueness: a dotted typeId is legal
 * here and, among the source positions, in `sourceCell` alone; and a plain column
 * may not sit alongside a dotted one whose FIRST-dot root it is — `parent` beside
 * `parent.sub` is two writers on one cell, which `cellNodeKey`'s root collapse
 * would then hide from the loop check.
 * The root is taken at the first dot alone, exactly as
 * `arFormulaTargetFieldSegments` splits a write address, so `parent.sub` and
 * `parent.sub.deep` are two distinct sub-fields of one cell — `fields['sub']` and
 * `fields['sub.deep']` — and neither overlaps the other.
 */
function explainProjectionColumns(columns: readonly unknown[]): FormDeriveDraftProblem[] {
  const declaredAliases = new Map<string, number>();
  const declared: FormDeriveDraftDeclaredColumn[] = [];
  const problems: FormDeriveDraftProblem[] = [];

  columns.forEach((column, columnIndex) => {
    if (!isRecord(column)) {
      problems.push({ kind: 'malformedColumn', columnIndex });
      return;
    }

    if (!isColumnPathTypeId(column.typeId)) {
      problems.push({
        kind: 'invalidColumnTypeId',
        columnIndex,
        typeId: typeof column.typeId === 'string' ? column.typeId : undefined,
      });
    } else {
      const first = declared.find((candidate) => candidate.typeId === column.typeId);
      if (first === undefined) {
        declared.push({ columnIndex, typeId: column.typeId });
      } else {
        problems.push({
          kind: 'duplicateColumn',
          columnIndex,
          typeId: column.typeId,
          firstIndex: first.columnIndex,
        });
      }
    }

    problems.push(...explainRowValueSource(column.value, columnIndex, new Set(declaredAliases.keys())));

    if (column.alias === undefined) return;
    if (typeof column.alias !== 'string' || !ALIAS_PATTERN.test(column.alias)) {
      problems.push({
        kind: 'invalidColumnAlias',
        columnIndex,
        alias: typeof column.alias === 'string' ? column.alias : undefined,
      });
      return;
    }
    if (reservedAliasNames.has(column.alias)) {
      problems.push({ kind: 'reservedColumnAlias', columnIndex, alias: column.alias });
      return;
    }
    const firstIndex = declaredAliases.get(column.alias);
    if (firstIndex !== undefined) {
      problems.push({
        kind: 'duplicateColumnAlias',
        columnIndex,
        alias: column.alias,
        firstIndex,
      });
      return;
    }
    declaredAliases.set(column.alias, columnIndex);
  });

  const overlaps = declared.flatMap(({ typeId: partTypeId }): FormDeriveDraftProblem[] => {
    const dotIndex = partTypeId.indexOf('.');
    if (dotIndex === -1) return [];
    const root = declared.find(
      (candidate) => candidate.typeId === partTypeId.slice(0, dotIndex),
    );
    return root === undefined
      ? []
      : [{ kind: 'columnRootOverlap', columnIndex: root.columnIndex, typeId: root.typeId, partTypeId }];
  });

  return [...problems, ...overlaps];
}

/**
 * A row projection: the partition key on the source table, the table it fills,
 * and the columns it declares there. `roundToCents` is per column on this arm, so
 * an entry-level flag is inadmissible.
 */
function explainProjection(value: Record<string, unknown>): FormDeriveDraftProblem[] {
  const { source, target } = value;

  const sourceProblems: FormDeriveDraftProblem[] =
    isRecord(source) && source.kind === 'colUniques'
      ? [
          ...(isNonEmptyString(source.tableId) ? [] : [malformedEntryProblem]),
          ...explainSourceTypeId(source.typeId, undefined),
        ]
      : [malformedEntryProblem];

  const targetProblems: FormDeriveDraftProblem[] =
    isRecord(target) &&
    !('fieldId' in target) &&
    isNonEmptyString(target.tableId) &&
    target.typeId === undefined
      ? []
      : [{ kind: 'malformedTarget' }];

  const selfProblems: FormDeriveDraftProblem[] =
    isRecord(source) &&
    isRecord(target) &&
    isNonEmptyString(source.tableId) &&
    source.tableId === target.tableId
      ? [{ kind: 'projectionSelfTable', tableId: source.tableId }]
      : [];

  const columns: unknown[] = Array.isArray(value.columns) ? value.columns : [];
  const columnProblems: FormDeriveDraftProblem[] =
    columns.length === 0 ? [{ kind: 'emptyColumns' }] : explainProjectionColumns(columns);

  return [
    ...sourceProblems,
    ...targetProblems,
    ...selfProblems,
    ...columnProblems,
    ...(value.roundToCents === undefined ? [] : [{ kind: 'roundToCentsInadmissible' as const }]),
  ];
}

function explainSingleSource(value: Record<string, unknown>): FormDeriveDraftProblem[] {
  const source = value.source;
  if (!isRecord(source)) return [malformedEntryProblem];

  const roundProblems: FormDeriveDraftProblem[] =
    source.kind === 'colAggregate'
      ? value.roundToCents === undefined || typeof value.roundToCents === 'boolean'
        ? []
        : [malformedEntryProblem]
      : value.roundToCents === undefined
        ? []
        : [{ kind: 'roundToCentsInadmissible' }];

  return [
    ...explainScalarSource(source),
    ...(isRecord(value.target) && isNonEmptyString(value.target.fieldId)
      ? []
      : [{ kind: 'malformedTarget' } as const]),
    ...roundProblems,
  ];
}

/**
 * Mirrors `isFormDeriveEntry`'s rule order; explanation only, never authority.
 * A `colUniques` source routes to the projection reading even without a `columns`
 * member, which the engine would drop as a malformed scalar source: "no columns
 * to fill" is the problem the author can act on, and this analysis decides no
 * verdict either way.
 */
function explainEntryShape(candidate: unknown): FormDeriveDraftProblem[] {
  if (!isRecord(candidate)) return [malformedEntryProblem];
  const idProblems: FormDeriveDraftProblem[] =
    typeof candidate.deriveId === 'string' && DERIVE_ID_PATTERN.test(candidate.deriveId)
      ? []
      : [
          {
            kind: 'invalidDeriveId',
            deriveId: typeof candidate.deriveId === 'string' ? candidate.deriveId : undefined,
          },
        ];
  const projectionShaped =
    'columns' in candidate ||
    (isRecord(candidate.source) && candidate.source.kind === 'colUniques');
  const bodyProblems = ((): FormDeriveDraftProblem[] => {
    if ('operands' in candidate) return explainAlgebraic(candidate);
    return projectionShaped ? explainProjection(candidate) : explainSingleSource(candidate);
  })();
  return dedupeProblems([...idProblems, ...bodyProblems]);
}

type FormDeriveDraftSurvivor = { entry: FormDeriveEntry; index: number };

/**
 * A shape-valid entry the engine dropped lost a dedupe: the engine's seen-sets
 * grow only on survivors, so the clash is always against an EARLIER survivor,
 * and the duplicate-target problem lands on the entry that would silently lose.
 */
function explainDroppedDuplicate(
  candidate: unknown,
  index: number,
  survivors: readonly FormDeriveDraftSurvivor[],
): FormDeriveDraftProblem[] {
  if (!isRecord(candidate)) return [];
  const earlier = survivors.filter((survivor) => survivor.index < index);
  const deriveId = candidate.deriveId;
  if (typeof deriveId === 'string') {
    const idClash = earlier.find((survivor) => survivor.entry.deriveId === deriveId);
    if (idClash !== undefined) {
      return [{ kind: 'duplicateDeriveId', deriveId, firstIndex: idClash.index }];
    }
  }
  const targetKey = formDeriveTargetKey(candidate.target);
  if (targetKey !== undefined) {
    const winner = earlier.find(
      (survivor) => formDeriveTargetKey(survivor.entry.target) === targetKey,
    );
    if (winner !== undefined) {
      return [
        { kind: 'duplicateTarget', winnerIndex: winner.index, winnerDeriveId: winner.entry.deriveId },
      ];
    }
  }
  return [];
}

/**
 * The ROOT column definition behind an address, because a composite sub-field has
 * none of its own on the table type — the same collapse `cellNodeKey` and
 * `resolveArFormulaOwnedTypeIds` apply.
 */
function resolveColumnRef(
  tableTypes: readonly ArTableType[],
  tableId: ArTableId,
  typeId: string,
): FormDeriveDraftColumnRef | undefined {
  const dotIndex = typeId.indexOf('.');
  const rootTypeId = dotIndex === -1 ? typeId : typeId.slice(0, dotIndex);
  const column = tableTypes
    .find((tableType) => tableType.docId === tableId)
    ?.columns.find((candidate) => candidate.typeId === rootTypeId);
  return column === undefined ? undefined : { tableId, typeId, column };
}

/**
 * The structural kind comparable without the field-type registry: field columns
 * are one kind; reference columns split by `referenceType` and entity. Two field
 * columns of different field types compare equal here — the coercion matrix is
 * unowned, and the warning carries both `column` definitions so a caller holding
 * the registry can judge finer without re-resolving.
 */
function columnValueShape(column: ArColumnDefinition): string {
  if (column.uiElementType === UIElementType.Field) return JSON.stringify(['field']);
  return JSON.stringify([
    'reference',
    column.referenceType,
    column.referenceType === 'entity' ? column.entityId : undefined,
  ]);
}

/** Every alias a guard of these columns reads, `left` and alias-shaped `right` alike. */
function guardReadAliases(columns: readonly FormDeriveProjectionColumn[]): Set<string> {
  return new Set(
    columns.flatMap(({ value }) =>
      value.kind === 'cases'
        ? value.cases.flatMap(({ when }) =>
            (when ?? []).flatMap((condition) => [
              condition.left,
              ...(typeof condition.right === 'object' && condition.right !== null
                ? [condition.right.alias]
                : []),
            ]),
          )
        : [],
    ),
  );
}

/**
 * The two arms that COPY a source cell into a declared column — `groupKey` from
 * the partition key, `sourceCell` from the group's winner row — are the ones a
 * shape mismatch is meaningful for. An aggregate is a number by construction, a
 * case yields an authored literal, and a reference property is judged against the
 * record, not the column.
 */
function collectEntryWarnings(
  entry: FormDeriveEntry,
  tableTypes: readonly ArTableType[],
): FormDeriveDraftWarning[] {
  if ('operands' in entry) {
    const used = new Set(readExpressionVariables(entry.expression) ?? []);
    return entry.operands.flatMap(({ alias }) =>
      used.has(alias) ? [] : [{ kind: 'unusedAlias' as const, alias }],
    );
  }
  if (!('columns' in entry)) return [];

  const read = guardReadAliases(entry.columns);
  const unusedAliases = entry.columns.flatMap(({ alias }) =>
    alias !== undefined && !read.has(alias) ? [{ kind: 'unusedAlias' as const, alias }] : [],
  );

  const mismatches = entry.columns.flatMap(
    ({ typeId, value }, columnIndex): FormDeriveDraftWarning[] => {
      const sourceTypeId =
        value.kind === 'groupKey'
          ? entry.source.typeId
          : value.kind === 'sourceCell'
            ? value.typeId
            : undefined;
      if (sourceTypeId === undefined) return [];
      const source = resolveColumnRef(tableTypes, entry.source.tableId, sourceTypeId);
      const target = resolveColumnRef(tableTypes, entry.target.tableId, typeId);
      if (source === undefined || target === undefined) return [];
      return columnValueShape(source.column) === columnValueShape(target.column)
        ? []
        : [{ kind: 'columnTypeMismatch', columnIndex, source, target }];
    },
  );

  return [...unusedAliases, ...mismatches];
}

/**
 * `detectFormDeriveCycle` mints every node key with `JSON.stringify` of a
 * kind-first tuple, so parsing is total in practice; an unreadable key is dropped
 * rather than thrown, because a loop the author must break is more useful
 * half-named than not reported at all.
 */
function decodeCycleNode(nodeKey: FormDeriveGraphNodeKey): FormDeriveDraftCycleNode[] {
  const parsed: unknown = ((): unknown => {
    try {
      return JSON.parse(nodeKey);
    } catch {
      return undefined;
    }
  })();
  if (!Array.isArray(parsed)) return [];
  const [kind, first, second] = parsed as unknown[];
  if (kind === 'field' && typeof first === 'string') return [{ kind: 'field', fieldId: first }];
  if (kind === 'rowset' && typeof first === 'string') return [{ kind: 'rowset', tableId: first }];
  if (kind === 'cell' && typeof first === 'string' && typeof second === 'string') {
    return [{ kind: 'cell', tableId: first, typeId: second }];
  }
  return [];
}

function validateFormDeriveDraft(input: ValidateFormDeriveDraftInput): FormDeriveDraftVerdict {
  const { entries, tableTypes } = input;

  const config = createFormDeriveEngineConfig({ formDerives: entries });

  const indexOfDraft = new Map<unknown, number>();
  entries.forEach((entry, index) => {
    if (!indexOfDraft.has(entry)) indexOfDraft.set(entry, index);
  });

  const survivors: FormDeriveDraftSurvivor[] = config.entries.flatMap((entry) => {
    const index = indexOfDraft.get(entry);
    return index === undefined ? [] : [{ entry, index }];
  });
  const survivorByIndex = new Map(survivors.map((survivor) => [survivor.index, survivor]));

  /**
   * The dropped entries this analysis can call neither malformed nor duplicated.
   * The engine's fold keeps every shape-valid, non-duplicate candidate and then
   * removes the chained reference hops from THAT set, so these entries and the
   * survivors together are the set `formDeriveChainedReferenceHopIds` judged. The
   * set is reassembled rather than re-derived: a second spelling of the hop rule
   * would accept an entry the engine silently drops. Each one already passed the
   * engine's own `isFormDeriveEntry`, which is what the annotation asserts.
   */
  const droppedShapeValid = entries.flatMap((candidate, index): FormDeriveEntry[] =>
    survivorByIndex.has(index) ||
    explainEntryShape(candidate).length > 0 ||
    explainDroppedDuplicate(candidate, index, survivors).length > 0
      ? []
      : [candidate as FormDeriveEntry],
  );
  const chainedHopDeriveIds = formDeriveChainedReferenceHopIds([
    ...config.entries,
    ...droppedShapeValid,
  ]);

  const unaddressableByIndex = new Map<number, string[]>(
    survivors.flatMap(({ entry, index }) => {
      const offending = [...new Set(formDeriveEntryTableIds(entry))].filter(
        (tableId) => !isAddressableId(tableId),
      );
      return offending.length === 0 ? [] : [[index, offending] as const];
    }),
  );

  const cycleSurvivors = survivors.filter(({ index }) => !unaddressableByIndex.has(index));

  const tableFormulaSequences = new Map<ArTableId, ArFormulaEntry[]>(
    tableTypes.flatMap((tableType) =>
      isNonEmptyString(tableType.docId)
        ? [[tableType.docId, tableType.formulaSequence ?? []] as const]
        : [],
    ),
  );

  const cycleVerdict = detectFormDeriveCycle({
    entries: cycleSurvivors.map(({ entry }) => entry),
    tableFormulaSequences,
  });

  const indexOfDeriveId = new Map<FormDeriveId, number>(
    cycleSurvivors.map(({ entry, index }) => [entry.deriveId, index]),
  );
  const cycles: FormDeriveDraftCycle[] = cycleVerdict.cycles.map((scc) => ({
    members: scc.deriveIds
      .flatMap((cyclicId) => {
        const index = indexOfDeriveId.get(cyclicId);
        return index === undefined ? [] : [{ index, deriveId: cyclicId }];
      })
      .sort((a, b) => a.index - b.index),
    nodes: scc.nodeKeys.flatMap(decodeCycleNode),
  }));

  const cycleIndexOfDeriveId = new Map<FormDeriveId, number>(
    cycleVerdict.cycles.flatMap((scc, cycleIndex) =>
      scc.deriveIds.map((cyclicId) => [cyclicId, cycleIndex] as const),
    ),
  );

  const entryVerdicts: FormDeriveDraftEntryVerdict[] = entries.map((candidate, index) => {
    const deriveId =
      isRecord(candidate) && typeof candidate.deriveId === 'string'
        ? candidate.deriveId
        : undefined;
    const survivor = survivorByIndex.get(index);

    if (survivor === undefined) {
      const shapeProblems = explainEntryShape(candidate);
      const problems =
        shapeProblems.length > 0
          ? shapeProblems
          : [
              ...explainDroppedDuplicate(candidate, index, survivors),
              ...(deriveId !== undefined && chainedHopDeriveIds.has(deriveId)
                ? [{ kind: 'chainedReferenceHop' as const }]
                : []),
            ];
      return {
        index,
        deriveId,
        status: 'invalid' as const,
        problems: problems.length > 0 ? problems : [{ kind: 'droppedByEngine' as const }],
        warnings: [],
        cycleIndex: undefined,
      };
    }

    const unaddressable = unaddressableByIndex.get(index);
    if (unaddressable !== undefined) {
      return {
        index,
        deriveId: survivor.entry.deriveId,
        status: 'invalid' as const,
        problems: unaddressable.map((tableId) => ({
          kind: 'unaddressableTableId' as const,
          tableId,
        })),
        warnings: [],
        cycleIndex: undefined,
      };
    }

    const cyclic = cycleVerdict.cyclicDeriveIds.has(survivor.entry.deriveId);
    const status: FormDeriveDraftEntryStatus = cycleVerdict.formulaOwnedDeriveIds.has(
      survivor.entry.deriveId,
    )
      ? 'targetFormulaOwned'
      : cyclic
        ? 'cycle'
        : 'active';

    return {
      index,
      deriveId: survivor.entry.deriveId,
      status,
      problems: [],
      warnings: collectEntryWarnings(survivor.entry, tableTypes),
      cycleIndex: status === 'cycle' ? cycleIndexOfDeriveId.get(survivor.entry.deriveId) : undefined,
    };
  });

  const order = cycleVerdict.order.flatMap((entry) => {
    const index = indexOfDraft.get(entry);
    return index === undefined ? [] : [index];
  });

  const invalidEntries = entryVerdicts.filter(({ status }) => status === 'invalid');
  const blocked =
    invalidEntries.length > 0 || cycles.length > 0 || cycleVerdict.missingTableIds.length > 0;

  return {
    blocked,
    entries: entryVerdicts,
    invalidEntries,
    cycles,
    missingTableIds: cycleVerdict.missingTableIds,
    order,
  };
}

export { validateFormDeriveDraft };
